import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2, MessageCircle } from "lucide-react";
import { claimUruPass, getUruPass } from "@/lib/api";

// Public URU Games pass page (no login, backend routes/uru_pass.py): the link in each participant's
// Comeback Pack. Pick a Bangalore gym and a start date, confirm, done. A claimed code only ever shows
// its confirmation. Their own details are filled in; "Passing this on to a friend?" clears them.

const input = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base text-[#f4f4f5] placeholder:text-[#6b6b78] [color-scheme:dark] focus:border-[#e5262b] focus:outline-none";
const day = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#06070b] text-[#f4f4f5]" style={{ fontFamily: "Inter, 'Helvetica Neue', Arial, sans-serif",
    backgroundImage: "radial-gradient(60% 40% at 50% 0%, rgba(229,38,43,0.18), transparent 70%)" }}>
    {/* Same look as www.liftr.fit: dark, Inter, LIFTR red. */}
    <div className="mx-auto max-w-md px-4 pb-16 pt-6">
      <div className="flex items-center justify-between">
        <span aria-label="LIFTR" className="block h-[19px] w-[100px] bg-[#e5262b]"
          style={{ WebkitMask: "url(/liftr-logo-mask.png) no-repeat left/contain", mask: "url(/liftr-logo-mask.png) no-repeat left/contain" }} />
        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-[#b4b4be]">URU Games Comeback Pack</span>
      </div>
      {children}
    </div>
  </div>
);

const WhatsApp = ({ head }) => head?.whatsapp ? (
  <a href={head.whatsapp} target="_blank" rel="noreferrer"
    className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#e5262b] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_30px_rgba(229,38,43,0.35)]">
    <MessageCircle className="h-4 w-4" /> WhatsApp {head.name.split(" ")[0]}
  </a>
) : null;

function Confirmation({ c }) {
  if (!c) return <p className="mt-8 text-lg">This pass has already been claimed.</p>;
  return (
    <div className="mt-6" data-testid="uru-confirmation">
      <h1 className="text-4xl font-bold leading-tight tracking-[-0.035em]">You're in, {c.first_name}.</h1>
      <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="text-sm font-semibold text-[#ff6b70]">LIFTR {c.gym}</div>
        <div className="mt-2 text-xl font-bold">{day(c.start_date)} to {day(c.end_date)}</div>
        <div className="mt-1 text-[#b4b4be]">Large group sessions, as many as you like.</div>
        {c.address && <div className="mt-3 text-sm text-[#9a9aa6]">{c.address}</div>}
      </div>
      <p className="mt-5 text-lg font-semibold">Show this at the front desk on your first visit.</p>
      <p className="mt-1 text-[#9a9aa6]">We've emailed it to you too.</p>
      {c.head?.name && (
        <div className="mt-5">
          <p className="text-[#b4b4be]">{c.head.name} runs the gym. Message them with any questions.</p>
          <WhatsApp head={c.head} />
        </div>
      )}
    </div>
  );
}

export default function UruPass() {
  const { code } = useParams();
  const [o, setO] = useState(null);
  const [missing, setMissing] = useState("");
  const [friend, setFriend] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", facility_id: "", start_date: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => { document.title = "URU Games x LIFTR | Your 2 weeks"; }, []);
  useEffect(() => {
    getUruPass(code).then((r) => {
      setO(r);
      if (r.claimed) setDone(r.confirmation || {});
      else setF((x) => ({ ...x, name: r.name, phone: r.phone, email: r.email, start_date: r.min_date }));
    }).catch((e) => setMissing(e?.response?.data?.detail || "This page isn't available right now."));
  }, [code]);

  const toggleFriend = () => {
    const on = !friend;
    setFriend(on);
    setF((x) => ({ ...x, ...(on ? { name: "", phone: "", email: "" } : { name: o.name, phone: o.phone, email: o.email }) }));
  };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.facility_id) { setError("Pick your gym."); return; }
    setBusy(true); setError("");
    try {
      const r = await claimUruPass(code, { ...f, friend });
      setDone(r.confirmation);
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Couldn't save your pass. Try again in a minute.");
    } finally { setBusy(false); }
  };

  if (missing) return <Shell><p className="mt-10 text-lg">{missing}</p></Shell>;
  if (!o) return <Shell><Loader2 className="mt-10 h-6 w-6 animate-spin" /></Shell>;
  if (done) return <Shell><Confirmation c={o.claimed ? o.confirmation : done} /></Shell>;
  return (
    <Shell>
      <h1 className="mt-8 text-4xl font-bold leading-[1.05] tracking-[-0.035em]">Your 2 weeks at LIFTR, <span className="text-[#9a9aa6]">{o.first_name}.</span></h1>
      <p className="mt-3 text-lg text-[#b4b4be]">From the URU Games Comeback Pack. Sorry about the rain.</p>

      <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
        <ul>{o.includes.map((t) => (
          <li key={t} className="flex items-start gap-2"><Check className="mt-1 h-4 w-4 shrink-0 text-[#ff6b70]" /><span>{t}</span></li>
        ))}</ul>
        <div className="mt-4 flex items-baseline gap-3">
          <span className="text-lg text-[#6b6b78] line-through">₹{o.list_price.toLocaleString("en-IN")} + GST</span>
          <span className="text-4xl font-bold tracking-[-0.03em]">₹0</span>
        </div>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div>
          <div className="mb-2 font-semibold">Pick your gym</div>
          <div className="space-y-2">
            {o.gyms.map((g) => (
              <label key={g.id} data-testid={`uru-gym-${g.id}`}
                className={`block cursor-pointer rounded-xl border p-4 transition ${f.facility_id === g.id ? "border-[#e5262b] bg-[#e5262b]/10" : "border-white/10 bg-white/5 hover:border-white/25"}`}>
                <input type="radio" name="gym" className="sr-only" checked={f.facility_id === g.id}
                  onChange={() => setF((x) => ({ ...x, facility_id: g.id }))} />
                <div className="font-semibold">LIFTR {g.name}</div>
                
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-2 block font-semibold" htmlFor="uru-start">Start date</label>
          <input id="uru-start" type="date" className={input} min={o.min_date} max={o.max_date} value={f.start_date} onChange={set("start_date")} required />
          <p className="mt-1 text-sm text-[#9a9aa6]">Start any day up to 31 Dec. Your 2 weeks run from then.</p>
        </div>
        <div className="space-y-3">
          <div className="font-semibold">{friend ? "Your friend's details" : "Your details"}</div>
          <input className={input} placeholder="Full name" value={f.name} onChange={set("name")} required />
          <input className={input} placeholder="Mobile number" inputMode="tel" value={f.phone} onChange={set("phone")} required />
          <input className={input} placeholder="Email" type="email" value={f.email} onChange={set("email")} required />
          <input className="hidden" tabIndex={-1} autoComplete="off" value={f.company} onChange={set("company")} />
          <button type="button" onClick={toggleFriend} className="text-sm font-semibold text-[#ff6b70] underline" data-testid="uru-friend">
            {friend ? "Use my own details" : "Passing this on to a friend? Enter their details"}
          </button>
        </div>
        {error && <p className="rounded-xl border border-[#e5262b]/40 bg-[#e5262b]/10 p-3 text-[#ff8a8e]">{error}</p>}
        <button type="submit" disabled={busy} className="h-12 w-full rounded-full bg-[#e5262b] text-base font-semibold text-white shadow-[0_8px_30px_rgba(229,38,43,0.35)] disabled:opacity-60">
          {busy ? "Saving..." : "Confirm my 2 weeks"}
        </button>
      </form>
    </Shell>
  );
}
